import { supabase } from '@/lib/supabase'
import { normalizeAuthorName } from '@/shared/utils/authorName'
import {
  fetchAnalysisPostAttachmentsByAnalysisIds,
  type AnalysisPostAttachment,
} from './analysisPostAttachmentsService'
import { normalizeLinkedOpportunityId } from '../utils/normalizeLinkedOpportunityId'
import { normalizeLinkedTimelineRootId } from '../utils/normalizeLinkedTimelineRootId'

export type AnalysisItem = {
  id: number
  authorId: string
  authorName: string
  authorAvatarUrl: string | null
  title: string
  contentHtml: string
  attachments: AnalysisPostAttachment[]
  linkedOpportunityId: number | null
  linkedTimelineRootId: number | null
  courtesyEnabled: boolean
  createdAt: string
  canView: boolean
  requiredPlan: AnalysisRequiredPlan | null
  peopleCount: number | null
}

export type AnalysisRequiredPlan = {
  id: string
  name: string
  amount_cents: number | null
  currency: string | null
  interval: string | null
}

export type AnalysisTimelineRootCandidate = {
  id: number
  title: string
  contentHtml: string
  authorName: string
  authorAvatarUrl: string | null
  createdAt: string
}

export async function fetchAnalysisEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_analysis_enabled' as any)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('user_analysis_enabled error', error)
      return false
    }
    return Boolean(data)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('user_analysis_enabled error', e)
    return false
  }
}

export async function fetchAnalysisEditorEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_analysis_editor_enabled' as any)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('user_analysis_editor_enabled error', error)
      return false
    }
    return Boolean(data)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('user_analysis_editor_enabled error', e)
    return false
  }
}

export async function createAnalysis(params: {
  title: string
  html: string
  linkedOpportunityId?: number | null
  linkedTimelineRootId?: number | null
  courtesyEnabled?: boolean
}): Promise<AnalysisItem | null> {
  const title = String(params.title || '').trim()
  const html = String(params.html || '').trim()
  if (!title || !html) return null

  const linkedOpportunityId = normalizeLinkedOpportunityId(params.linkedOpportunityId)
  const linkedTimelineRootId = normalizeLinkedTimelineRootId(params.linkedTimelineRootId)
  const effectiveTimelineRootId = linkedOpportunityId ? null : linkedTimelineRootId
  const courtesyEnabled = Boolean(params.courtesyEnabled)

  const auth = await supabase.auth.getUser()
  const user = auth.data.user
  const userId = user?.id
  if (!userId) throw new Error('Usuário não autenticado')

  const meta = (user?.user_metadata || {}) as any

  let profileName: string | null = null
  let profileAvatarUrl: string | null | undefined

  try {
    const { data: profile, error: profileError } = await supabase
      .from('profiles' as any)
      .select('full_name,avatar_url')
      .eq('id', userId)
      .maybeSingle()

    if (!profileError && profile) {
      profileName = (profile as any).full_name ?? null
      if (
        typeof (profile as any).avatar_url === 'string' ||
        (profile as any).avatar_url === null
      ) {
        profileAvatarUrl = (profile as any).avatar_url as string | null
      }
    }
  } catch {
    // Em caso de falha, seguimos apenas com metadados do auth
  }

  const rawName =
    (profileName && String(profileName).trim().length > 0 ? profileName : null) ??
    (meta.full_name as string | null | undefined) ??
    (meta.name as string | null | undefined) ??
    (user?.email as string | null | undefined) ??
    null

  const authorName = normalizeAuthorName(rawName)

  let authorAvatarUrl: string | null = null
  if (profileAvatarUrl !== undefined) {
    authorAvatarUrl = profileAvatarUrl
  } else if (typeof meta.avatar_url === 'string' || meta.avatar_url === null) {
    authorAvatarUrl = meta.avatar_url as string | null
  } else {
    authorAvatarUrl = null
  }

  const { data, error } = await supabase
    .from('market_analyses' as any)
    .insert({
      author_id: userId,
      author_name: authorName,
      author_avatar_url: authorAvatarUrl,
      title,
      content_html: html,
      linked_opportunity_id: linkedOpportunityId,
      linked_timeline_root_id: effectiveTimelineRootId,
      courtesy_enabled: courtesyEnabled,
    } as any)
    .select('id,author_id,author_name,author_avatar_url,title,content_html,linked_opportunity_id,linked_timeline_root_id,courtesy_enabled,created_at')
    .single()

  if (error) {
    // eslint-disable-next-line no-console
    console.error('createAnalysis error', error)
    throw error
  }

  const [item] = await mapRowsToAnalysisItems([data as any])
  return item ?? null
}

export async function listAnalyses(params: {
  page: number
  pageSize?: number
}): Promise<{ items: AnalysisItem[] }> {
  const page = Math.max(1, Number(params.page) || 1)
  const pageSize = Math.max(1, Number(params.pageSize) || 25)
  const { data, error } = await supabase.rpc('list_market_analyses_feed' as any, {
    p_page: page,
    p_page_size: pageSize,
  })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('listAnalyses error', error)
    throw error
  }

  const rows = Array.isArray(data) ? data : []
  const allowedRows = rows.filter((row: any) => Boolean(row?.can_view))
  const allowedItems = await mapRowsToAnalysisItems(allowedRows)
  const allowedById = new Map<number, AnalysisItem>()
  allowedItems.forEach((it) => {
    allowedById.set(Number(it.id), it)
  })

  const requiredPlanFromRow = (row: any): AnalysisRequiredPlan | null => {
    if (!row?.required_plan_id) return null
    return {
      id: String(row.required_plan_id || ''),
      name: String(row.required_plan_name || '').trim(),
      amount_cents:
        row.required_plan_amount_cents != null
          ? Number(row.required_plan_amount_cents)
          : null,
      currency: row.required_plan_currency ? String(row.required_plan_currency) : null,
      interval: row.required_plan_interval ? String(row.required_plan_interval) : null,
    }
  }

  const items = rows.map((row: any) => {
    const id = Number(row?.id)
    const canView = Boolean(row?.can_view)
    if (canView) {
      const base = allowedById.get(id)
      if (base) return base
    }
    return {
      id: Number.isFinite(id) ? id : 0,
      authorId: '',
      authorName: '',
      authorAvatarUrl: null,
      title: '',
      contentHtml: '',
      attachments: [],
      linkedOpportunityId: null,
      linkedTimelineRootId: normalizeLinkedTimelineRootId(row?.linked_timeline_root_id ?? null),
      courtesyEnabled: false,
      createdAt: row?.created_at ? String(row.created_at) : '',
      canView: false,
      requiredPlan: requiredPlanFromRow(row),
      peopleCount:
        row?.people_count != null ? Number(row.people_count) : null,
    } as AnalysisItem
  })

  const allowedIds = allowedItems.map((it) => it.id)
  if (allowedIds.length > 0) {
    const attachmentsMap = await fetchAnalysisPostAttachmentsByAnalysisIds(allowedIds)
    for (const item of items) {
      if (!item.canView) continue
      item.attachments = attachmentsMap[String(item.id)] || []
    }
  }

  return { items }
}

export async function listAnalysesByLinkedOpportunityId(params: {
  opportunityId: number
}): Promise<AnalysisItem[]> {
  const opportunityId = normalizeLinkedOpportunityId(params.opportunityId)
  if (!opportunityId) return []

  const { data, error } = await supabase
    .from('market_analyses' as any)
    .select('id,author_id,author_name,author_avatar_url,title,content_html,linked_opportunity_id,linked_timeline_root_id,courtesy_enabled,created_at')
    .eq('linked_opportunity_id', opportunityId)
    .order('created_at', { ascending: false })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('listAnalysesByLinkedOpportunityId error', error)
    throw error
  }

  const items = await mapRowsToAnalysisItems(data as any)
  const ids = items.map((it) => it.id)
  if (ids.length > 0) {
    const attachmentsMap = await fetchAnalysisPostAttachmentsByAnalysisIds(ids)
    for (const item of items) {
      item.attachments = attachmentsMap[String(item.id)] || []
    }
  }

  return items
}

export async function listAnalysesByTimelineRootId(params: {
  timelineRootId: number
}): Promise<AnalysisItem[]> {
  const timelineRootId = normalizeLinkedTimelineRootId(params.timelineRootId)
  if (!timelineRootId) return []

  const { data, error } = await supabase
    .from('market_analyses' as any)
    .select('id,author_id,author_name,author_avatar_url,title,content_html,linked_opportunity_id,linked_timeline_root_id,courtesy_enabled,created_at')
    .or(`id.eq.${timelineRootId},linked_timeline_root_id.eq.${timelineRootId}`)
    .order('created_at', { ascending: false })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('listAnalysesByTimelineRootId error', error)
    throw error
  }

  const items = await mapRowsToAnalysisItems(data as any)
  const ids = items.map((it) => it.id)
  if (ids.length > 0) {
    const attachmentsMap = await fetchAnalysisPostAttachmentsByAnalysisIds(ids)
    for (const item of items) {
      item.attachments = attachmentsMap[String(item.id)] || []
    }
  }

  return items
}

export async function listTimelineRootAnalyses(): Promise<AnalysisTimelineRootCandidate[]> {
  const { data, error } = await supabase
    .from('market_analyses' as any)
    .select('id,title,content_html,author_name,author_avatar_url,created_at')
    .is('linked_opportunity_id', null)
    .is('linked_timeline_root_id', null)
    .order('created_at', { ascending: false })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('listTimelineRootAnalyses error', error)
    throw error
  }

  return (Array.isArray(data) ? data : []).map((row: any) => ({
    id: Number(row.id),
    title: String(row.title || '').trim(),
    contentHtml: String(row.content_html || ''),
    authorName: normalizeAuthorName(row.author_name),
    authorAvatarUrl:
      typeof row.author_avatar_url === 'string' || row.author_avatar_url === null
        ? (row.author_avatar_url as string | null)
        : null,
    createdAt: row.created_at ? String(row.created_at) : '',
  }))
}

export async function updateAnalysis(
  id: number,
  params: {
    title: string
    html: string
    linkedOpportunityId?: number | null
    linkedTimelineRootId?: number | null
    courtesyEnabled?: boolean
  },
): Promise<void> {
  const analysisId = Number(id)
  if (!Number.isFinite(analysisId) || analysisId <= 0) return

  const title = String(params.title || '').trim()
  const html = String(params.html || '').trim()
  if (!title || !html) return

  const payload: any = {
    title,
    content_html: html,
  }

  if (params.linkedOpportunityId !== undefined) {
    payload.linked_opportunity_id = normalizeLinkedOpportunityId(params.linkedOpportunityId)
  }
  if (params.linkedTimelineRootId !== undefined) {
    payload.linked_timeline_root_id = normalizeLinkedTimelineRootId(params.linkedTimelineRootId)
  }
  if (params.courtesyEnabled !== undefined) {
    payload.courtesy_enabled = Boolean(params.courtesyEnabled)
  }

  const { error } = await supabase
    .from('market_analyses' as any)
    .update(payload)
    .eq('id', analysisId)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('updateAnalysis error', error)
    throw error
  }
}

export async function deleteAnalysis(id: number): Promise<void> {
  const analysisId = Number(id)
  if (!Number.isFinite(analysisId) || analysisId <= 0) return

  const { error } = await supabase
    .from('market_analyses' as any)
    .delete()
    .eq('id', analysisId)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('deleteAnalysis error', error)
    throw error
  }
}

async function mapRowsToAnalysisItems(rowsRaw: any): Promise<AnalysisItem[]> {
  const rows = Array.isArray(rowsRaw) ? rowsRaw : []
  if (!rows.length) return []

  const authorIds = Array.from(
    new Set(
      rows
        .map((row: any) => (row.author_id ? String(row.author_id) : ''))
        .filter((id: string) => !!id),
    ),
  )

  const profilesMap: Record<
    string,
    {
      name: string
      avatarUrl: string | null
    }
  > = {}

  if (authorIds.length > 0) {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles' as any)
        .select('id,full_name,avatar_url')
        .in('id', authorIds as any)

      if (profilesError) {
        // eslint-disable-next-line no-console
        console.error('listAnalyses profiles error', profilesError)
      } else if (Array.isArray(profiles)) {
        profiles.forEach((p: any) => {
          const id = p.id ? String(p.id) : ''
          if (!id) return
          profilesMap[id] = {
            name: String(p.full_name || '').trim(),
            avatarUrl:
              typeof p.avatar_url === 'string' || p.avatar_url === null
                ? (p.avatar_url as string | null)
                : null,
          }
        })
      }
    } catch {
      // Em caso de erro, seguimos apenas com os snapshots de market_analyses
    }
  }

  const items: AnalysisItem[] = rows.map((row: any) => {
    const authorId = row.author_id ? String(row.author_id) : ''
    const profileOverride = authorId ? profilesMap[authorId] : undefined

    const overrideName = profileOverride?.name?.trim()
    const rawName =
      (overrideName && overrideName.length > 0 ? overrideName : null) ??
      (typeof row.author_name === 'string' ? (row.author_name as string) : null)

    const authorName = normalizeAuthorName(rawName)

    let authorAvatarUrl: string | null = null
    if (profileOverride && profileOverride.avatarUrl !== undefined) {
      authorAvatarUrl = profileOverride.avatarUrl
    } else if (
      typeof row.author_avatar_url === 'string' ||
      row.author_avatar_url === null
    ) {
      authorAvatarUrl = row.author_avatar_url as string | null
    }

    return {
      id: Number(row.id),
      authorId,
      authorName,
      authorAvatarUrl,
      title: String(row.title || '').trim(),
      contentHtml: String(row.content_html || ''),
      attachments: [],
      linkedOpportunityId: normalizeLinkedOpportunityId((row as any).linked_opportunity_id),
      linkedTimelineRootId: normalizeLinkedTimelineRootId((row as any).linked_timeline_root_id),
      courtesyEnabled: Boolean((row as any).courtesy_enabled),
      createdAt: row.created_at ? String(row.created_at) : '',
      canView: true,
      requiredPlan: null,
      peopleCount: null,
    }
  })

  return items
}
